/** Blue-tint panel fill (no shadow) — prompt wrap, context headers, hints, assistant bubbles. */
import { colors } from '../../../designTokens';

export const CAPABILITY_GLOW_SURFACE = {
  bgcolor: 'rgba(29, 78, 216, 0.045)',
  borderColor: 'divider',
  hoverBgcolor: 'rgba(29, 78, 216, 0.07)',
};

/** Shared elevation for chat message cards — subtle separation, not floating. */
export const CHAT_MESSAGE_ELEVATION = '0 1px 2px rgba(0, 0, 0, 0.04)';

/** AI assistant message bubbles (execute, freestyle, builder). */
export const ASSISTANT_CHAT_SURFACE = {
  bgcolor: '#F3F4F6',
  borderColor: '#E5E7EB',
  color: colors.textPrimary,
  boxShadow: CHAT_MESSAGE_ELEVATION,
};

/** User message bubbles — ~65% of brand blue intensity: distinct but not CTA-loud. */
export const USER_CHAT_SURFACE = {
  /** Between brand #1d4ed8 and muted #4E7FCF — secondary focal point in the thread. */
  bgcolor: '#3D6FD6',
  color: '#ffffff',
  lineHeight: 1.55,
  timestampColor: 'rgba(255, 255, 255, 0.7)',
  boxShadow: CHAT_MESSAGE_ELEVATION,
};

/** PromptCrew chat grey — user bubbles and legacy assistant bubble fill. */
export const ASSISTANT_CHAT_GREY = '#F3F4F6';

/** Assistant thread background — matches side panel / shelf surface. */
export const ASSISTANT_THREAD_BG = colors.sidebarSurface;

/** Shared content well fill — modal/builder/knowledge editor bodies on assistant grey. */
export const ASSISTANT_CONTENT_WELL_SX = {
  bgcolor: ASSISTANT_THREAD_BG,
};

/** Execute / Builder message composer — blue tint + soft shadow on input only. */
export const CHAT_COMPOSER_SURFACE = {
  borderColor: 'rgba(148, 163, 184, 0.55)',
  bgcolor: 'rgba(29, 78, 216, 0.035)',
  boxShadow: '0 3px 10px rgba(29, 78, 216, 0.08)',
};

/** Assistant variant — user bubbles use the same light blue tint as chat composer. */
export const ASSISTANT_USER_CHAT_SURFACE = {
  bgcolor: CHAT_COMPOSER_SURFACE.bgcolor,
  color: colors.textPrimary,
  lineHeight: 1.55,
  timestampColor: colors.textSecondary,
  boxShadow: CHAT_COMPOSER_SURFACE.boxShadow,
  border: `1px solid ${CHAT_COMPOSER_SURFACE.borderColor}`,
  borderRadius: '18px',
};

/** Assistant variant — flat assistant messages (no bubble chrome). */
export const ASSISTANT_FLAT_ASSISTANT_SURFACE = {
  bgcolor: 'transparent',
  color: colors.textPrimary,
  borderColor: 'transparent',
  boxShadow: 'none',
};

/** Assistant landing + thread composer — white card on sidebar-grey background. */
export const ASSISTANT_COMPOSER_SURFACE = {
  borderColor: 'rgba(0, 0, 0, 0.08)',
  bgcolor: '#FFFFFF',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
  focusBorderColor: 'rgba(29, 78, 216, 0.6)',
  focusBoxShadow: '0 0 0 2px rgba(29, 78, 216, 0.08)',
};

/** Assistant chat column — slightly narrower than full execute width. */
export const ASSISTANT_CONVERSATION_MAX_WIDTH = 780;

/** Centered column for assistant landing + thread (messages + composer). */
export const ASSISTANT_CONVERSATION_COLUMN_SX = {
  width: '100%',
  maxWidth: ASSISTANT_CONVERSATION_MAX_WIDTH,
  mx: 'auto',
};

/** Centered column for execute chat (full-width panels). */
export const CONVERSATION_COLUMN_SX = {
  width: '100%',
  maxWidth: 1331,
  mx: 'auto',
};

/** Assistant composer can be slightly wider than message column. */
export const ASSISTANT_COMPOSER_MAX_WIDTH = ASSISTANT_CONVERSATION_MAX_WIDTH;

/** Chat theme bundles — surfaces + layout + markdown policy. */
export const CHAT_THEMES = {
  default: {
    id: 'default',
    threadBg: undefined,
    userSurface: USER_CHAT_SURFACE,
    assistantSurface: ASSISTANT_CHAT_SURFACE,
    composerSurface: ASSISTANT_COMPOSER_SURFACE,
    conversationColumnSx: CONVERSATION_COLUMN_SX,
    renderUserAsMarkdown: false,
  },
  assistant: {
    id: 'assistant',
    threadBg: ASSISTANT_THREAD_BG,
    userSurface: ASSISTANT_USER_CHAT_SURFACE,
    assistantSurface: ASSISTANT_FLAT_ASSISTANT_SURFACE,
    composerSurface: ASSISTANT_COMPOSER_SURFACE,
    conversationColumnSx: ASSISTANT_CONVERSATION_COLUMN_SX,
    renderUserAsMarkdown: true,
  },
};

/** Resolve a chat theme by variant id. */
export function getChatTheme(variant = 'default') {
  return CHAT_THEMES[variant] || CHAT_THEMES.default;
}
